import React, { useState } from "react";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Mail, MessageSquare, UserPlus, Loader2, Copy, Share2 } from "lucide-react";
import { shareLink } from "../lib/share";
import { toast } from "sonner";

export const looksLikeIdentifier = (v) => {
  const s = (v || "").trim();
  if (s.includes("@")) return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  return s.replace(/\D/g, "").length >= 10;
};

const smsUrl = (number, body) => {
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
  return `sms:${number}${ios ? "&" : "?"}body=${encodeURIComponent(body)}`;
};

// "This person is not on OnSiteComms yet — invite them?" (e-mail => we send it; phone => native SMS composer)
export const InviteDialog = ({ identifier, kind = "connect", onClose }) => {
  const { t } = useApp();
  const [busy, setBusy] = useState(false);
  const [sms, setSms] = useState(null); // {to, text}
  const isEmail = (identifier || "").includes("@");
  const open = !!identifier;

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/invites/external", { identifier, kind });
      if (data.channel === "email") {
        toast.success(`${t("invite_email_sent")} ${data.to}`);
        onClose();
      } else {
        setSms(data);
        window.location.href = smsUrl(data.to, data.text);
      }
    } catch (err) {
      const d = err?.response?.data?.detail || "";
      if (d === "already_registered") toast.info(t("invite_already_registered"));
      else if (d === "email_not_configured") toast.error(t("invite_email_unavailable"));
      else if (d === "invalid_identifier") toast.error(t("invite_invalid"));
      else toast.error(t("something_wrong"));
    } finally { setBusy(false); }
  };

  const share = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/invites/external", { identifier, kind, dry_run: true });
      // Attach the invitation flyer when the platform can share files (WhatsApp shows image + caption)
      let r = null;
      try {
        if (navigator.share && navigator.canShare) {
          const blob = await (await fetch("/invite-flyer.jpg")).blob();
          const file = new File([blob], "onsitecomms-invite.jpg", { type: "image/jpeg" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: data.subject, text: data.text });
            r = "shared";
          }
        }
      } catch (e) {
        if (e && (e.name === "AbortError" || e.name === "NotAllowedError")) r = "cancelled";
      }
      if (!r) r = await shareLink({ title: data.subject, text: data.text.replace(data.link, "").trim(), url: data.link });
      if (r === "shared") onClose();
      else if (r !== "cancelled") {
        try { await navigator.clipboard.writeText(data.text); toast.success(t("copied")); } catch (e) { /* ignore */ }
        setSms({ ...data, fromShare: true });
      }
    } catch (err) {
      toast.error(t("something_wrong"));
    } finally { setBusy(false); }
  };

  const copyText = () => {
    try { navigator.clipboard.writeText(sms.text); toast.success(t("copied")); } catch (e) { /* ignore */ }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setSms(null); onClose(); } }}>
      <DialogContent className="max-w-sm" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-primary" />{t("invite_title")}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground" data-testid="invite-text">
          <span className="font-semibold text-foreground break-all">{identifier}</span> {t("invite_not_found")}
        </p>
        {sms ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">{sms.fromShare ? t("invite_share_hint") : t("invite_sms_hint")}</p>
            <div className="rounded-lg border bg-muted/40 p-2.5 text-xs" data-testid="invite-sms-text">{sms.text}</div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" className="flex-1" onClick={copyText} data-testid="invite-copy-btn"><Copy className="h-3.5 w-3.5 mr-1" />{t("copy")}</Button>
              {sms.to && !sms.fromShare && <Button type="button" size="sm" className="flex-1" onClick={() => { window.location.href = smsUrl(sms.to, sms.text); }} data-testid="invite-sms-again-btn"><MessageSquare className="h-3.5 w-3.5 mr-1" />{t("invite_open_sms")}</Button>}
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">{isEmail ? t("invite_email_explain") : t("invite_sms_explain")}</p>
        )}
        <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          {!sms && (
            <Button type="button" className="w-full" onClick={send} disabled={busy} data-testid="invite-send-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isEmail ? <Mail className="h-4 w-4 mr-1.5" /> : <MessageSquare className="h-4 w-4 mr-1.5" />}
              {isEmail ? t("invite_send_email") : t("invite_send_sms")}
            </Button>
          )}
          <Button type="button" variant="secondary" className="w-full" onClick={share} disabled={busy} data-testid="invite-share-btn">
            <Share2 className="h-4 w-4 mr-1.5" />{t("invite_share")}
          </Button>
          <Button type="button" variant="outline" className="w-full" onClick={() => { setSms(null); onClose(); }} data-testid="invite-cancel-btn">{sms ? t("close") : t("cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
